/**
 * Бренды организации.
 *
 * Экран отвечает на один вопрос: **чем набит тариф**. Единица тарификации —
 * отслеживаемый вопрос (спека §10), квота покупается на организацию и делится
 * между пространствами, поэтому колонка «ядро» здесь важнее названия: агентство
 * двигает вопросы между клиентами и должно видеть, у кого сколько.
 *
 * Таблица настоящая, а не сетка из `div`: сравнение по колонкам — весь смысл
 * этого экрана (app-kit.md §3). Числовые колонки выровнены вправо, «не
 * измерено» пишется словами, а не нулём, а подпись `<caption>` несёт основание —
 * из какой организации взяты строки и сколько занято квоты.
 *
 * Архив стоит вкладкой, а не исчезает: из рабочих поверхностей архивный бренд
 * убран весь, но заведённое клиентом не пропадает бесследно — история замеров
 * по нему цела, и попасть в неё можно отсюда.
 *
 * Удаление стоит на строке, а не только в карточке, и это не удобство: клиенту
 * агентства, у которого пять заведённых по ошибке брендов, иначе пришлось бы
 * пять раз зайти в карточку и пять раз вернуться. Поток тот же самый
 * (`remove-brand.tsx`) — два места вызова, один код.
 */

import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAccess } from '../../session';
import { useScope, type Brand } from '../../scope';
import { ErrorNotice, Empty, Skeleton } from '../../ui/state';
import { Confirm } from '../../ui/fields';
import { Tabs } from '../../ui/Tabs';
import { compare, nextSort, SortHeader, type Sort } from '../../ui/table';
import { brandStatus } from '../../ui/labels';
import {
  dropRemovedBrand,
  removeBrand,
  RemoveConfirmBody,
  useStillHere,
} from './remove-brand';
import { domainProse, int, NOT_MEASURED } from '@shared/src/format';

type Column = 'name' | 'workspace' | 'questions' | 'competitors';

/** Что показывает таблица: работающие бренды или архив. */
type View = 'live' | 'archived';

export function BrandList() {
  const scope = useScope();
  const access = useAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const stillHere = useStillHere();
  const [sort, setSort] = useState<Sort<Column>>({ key: 'name', direction: 'asc' });
  /**
   * С какой вкладки экран открывается.
   *
   * Обычно — «В работе»: за ней работа и идёт. Исключение приезжает состоянием
   * перехода: с экрана «Клиенты» на бренды клиента, у которого живых брендов не
   * осталось, ведёт кнопка «Открыть архив брендов» — и открыть ей полагается
   * именно архив, иначе человек попадает на пустую вкладку и обещание кнопки не
   * сдержано. Соврать состояние не может: пустая вкладка «Архив» схлопывается
   * сама (`shown` ниже), так что «архив» из чужого перехода никого не запрёт.
   */
  const [view, setView] = useState<View>(() =>
    (location.state as { view?: View } | null)?.view === 'archived' ? 'archived' : 'live',
  );
  /** Какой бренд сейчас в окне подтверждения; `null` — окна нет. */
  const [removing, setRemoving] = useState<Brand | null>(null);
  /* Занятость — НАБОР, а не один идентификатор. Удаления независимы: пока летит
     запрос по одному бренду, человек успевает подтвердить второй, и один
     `string | null` тут же соврал бы дважды — перезаписался бы на второй бренд,
     а завершение первого сняло бы занятость со второго, оживив его кнопку
     посреди запроса. Тот же приём, что на экране клиентов. */
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const [failure, setFailure] = useState<unknown>(null);

  const workspaceTitle = useMemo(() => {
    const list = scope.state.kind === 'ready' ? scope.state.workspaces : [];
    return (id: string) => {
      const found = list.find((w) => w.id === id);
      return found ? (found.client_display_name ?? found.name) : '—';
    };
  }, [scope.state]);

  // Архив опустел под руками — смена клиента в переключателе меняет и его.
  // Вкладок в этот момент уже нет, и остаться на архивной значило бы запереть
  // человека на экране, с которого некуда нажать.
  const shown: View =
    view === 'archived' && scope.archivedBrands.length === 0 ? 'live' : view;

  // Архив приходит из области отдельным списком: правило «какое пространство
  // выбрано» у обоих одно, и повторять его здесь нельзя — разъедется.
  const source = shown === 'archived' ? scope.archivedBrands : scope.visibleBrands;

  const rows = useMemo(() => {
    const brands = [...source];
    return brands.sort((a, b) => {
      switch (sort.key) {
        case 'workspace':
          return compare(
            workspaceTitle(a.workspace_id),
            workspaceTitle(b.workspace_id),
            sort.direction,
          );
        case 'questions':
          return compare(a.questions_active, b.questions_active, sort.direction);
        case 'competitors':
          return compare(a.competitors_tracked, b.competitors_tracked, sort.direction);
        default:
          return compare(a.name, b.name, sort.direction);
      }
    });
  }, [source, sort, workspaceTitle]);

  const canWrite = access.reaches('POST', '/brands');
  // Убрать бренд может тот же, кто выводит его из работы: владелец и аналитик.
  // Спрашивается ровно тот путь контракта, которым уйдёт запрос, — опечатка в
  // шаблоне молча провалилась бы в глобовое правило `/brands/**` и открыла бы
  // кнопку там, где сервер откажет.
  const canRemove = access.reaches('POST', '/brands/{brandId}/hide');

  /**
   * Сказанное после удаления приезжает вместе с переходом.
   *
   * Строку кладёт `removeBrand` — и когда удаляют отсюда, и когда из карточки:
   * там экран закрывается, и любое собственное состояние вместе с ним. Молча
   * исчезнувшая строка читается как сбой, особенно на ветке `404`, где сервер
   * не сказал ни слова.
   */
  const removed = (location.state as { removed?: string } | null)?.removed ?? null;

  const done = removed ? (
    <p className="notice ok" role="status">
      <b>Готово</b>
      {removed}
    </p>
  ) : null;

  /**
   * Подтверждение удаления. Окно закрывается ПЕРВОЙ строкой: у `Confirm` нет ни
   * `busy`, ни `disabled`, и второй щелчок по той же кнопке ушёл бы вторым
   * запросом — то есть `404` поверх удавшегося удаления.
   */
  function confirmRemove() {
    const brand = removing;
    setRemoving(null);
    if (brand) void remove(brand);
  }

  async function remove(brand: Brand) {
    setBusy((keys) => new Set(keys).add(brand.id));
    setFailure(null);
    try {
      await removeBrand(
        { id: brand.id, name: brand.name },
        {
          dropSelection: () => dropRemovedBrand(scope, brand.id),
          reload: () => scope.reload(),
          // Уходим на список, только если человек всё ещё здесь: ответ приходит
          // позже, и за это время он мог уйти сам.
          navigate: (to, options) => {
            if (stillHere()) navigate(to, options);
          },
        },
      );
    } catch (cause) {
      setFailure(cause);
    } finally {
      setBusy((keys) => {
        const rest = new Set(keys);
        rest.delete(brand.id);
        return rest;
      });
    }
  }

  const head = (
    <div className="screen-head">
      <h1>Бренды</h1>
      {canWrite ? (
        <Link className="btn btn-primary btn-sm" to="/brands/new">
          Завести бренд
        </Link>
      ) : null}
    </div>
  );

  // Пустой вкладки «Архив» не бывает: пока архивировать было нечего, слово о
  // нём — лишний выбор, за которым ничего нет.
  const tabs =
    scope.archivedBrands.length > 0 ? (
      <Tabs<View>
        label="Какие бренды показывать"
        active={shown}
        onChange={setView}
        tabs={[
          { key: 'live', title: 'В работе', count: scope.visibleBrands.length },
          { key: 'archived', title: 'Архив', count: scope.archivedBrands.length },
        ]}
      />
    ) : null;

  if (scope.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }

  if (scope.state.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice
          error={scope.state.error}
          action={
            <button className="btn btn-ghost btn-sm" type="button" onClick={scope.reload}>
              Повторить
            </button>
          }
        />
      </>
    );
  }

  const place = scope.workspace
    ? (scope.workspace.client_display_name ?? scope.workspace.name)
    : null;

  if (rows.length === 0) {
    return (
      <>
        {head}
        {tabs}
        {done}
        {shown === 'archived' ? (
          <Empty eyebrow="Архив" title="В архиве пусто">
            Здесь лежат бренды, выведенные из работы. Их история замеров остаётся, но
            новых не будет.
          </Empty>
        ) : (
          <Empty
            eyebrow="Бренды"
            title={
              scope.archivedBrands.length > 0
                ? place
                  ? `В пространстве «${place}» действующих брендов нет`
                  : 'Действующих брендов не осталось'
                : place
                  ? `В пространстве «${place}» брендов нет`
                  : 'Ни одного бренда'
            }
            action={
              canWrite ? (
                <Link className="btn btn-primary" to="/brands/new">
                  Завести бренд
                </Link>
              ) : undefined
            }
          >
            {scope.archivedBrands.length > 0
              ? 'Заведённое лежит в архиве — история замеров там цела. Замеры идут только по брендам в работе.'
              : 'Бренд — это объект замера: имя, синонимы, сайт и города. Мы разберём сайт и предложим всё это сами, а вы поправите.'}
          </Empty>
        )}
      </>
    );
  }

  return (
    <>
      {head}
      {tabs}
      {done}
      {failure ? <ErrorNotice error={failure} /> : null}

      <div className="tbl-wrap">
        <table className="mini">
          <caption>
            {shown === 'archived'
              ? place
                ? `Архив брендов пространства «${place}»`
                : 'Архив брендов организации'
              : place
                ? `Бренды пространства «${place}»`
                : 'Бренды организации'}
          </caption>
          <thead>
            <tr>
              <SortHeader
                column="name"
                sort={sort}
                onSort={(k) => setSort(nextSort(sort, k))}
              >
                Бренд
              </SortHeader>
              <SortHeader
                column="workspace"
                sort={sort}
                onSort={(k) => setSort(nextSort(sort, k))}
              >
                Пространство
              </SortHeader>
              <SortHeader
                column="questions"
                sort={sort}
                onSort={(k) => setSort(nextSort(sort, k))}
                numeric
              >
                Вопросов
              </SortHeader>
              <SortHeader
                column="competitors"
                sort={sort}
                onSort={(k) => setSort(nextSort(sort, k))}
                numeric
              >
                Конкурентов
              </SortHeader>
              <th scope="col">Состояние</th>
              {canRemove ? (
                <th scope="col">
                  <span className="visually-hidden">Действия</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((brand) => (
              <tr key={brand.id}>
                <td>
                  <Link className="row-title" to={`/brands/${brand.id}`}>
                    {brand.name}
                  </Link>
                  <span className="stamp">
                    {brand.domain ? domainProse(brand.domain) : 'сайт не добавлен'}
                  </span>
                </td>
                <td>{workspaceTitle(brand.workspace_id)}</td>
                <td className="tnum tnum-col">
                  {brand.questions_active === undefined ? (
                    <span className="dim">{NOT_MEASURED}</span>
                  ) : (
                    int(brand.questions_active)
                  )}
                </td>
                <td className="tnum tnum-col">
                  {brand.competitors_tracked === undefined ? (
                    <span className="dim">{NOT_MEASURED}</span>
                  ) : (
                    int(brand.competitors_tracked)
                  )}
                </td>
                <td>{brandStatus(brand.status)}</td>
                {canRemove ? (
                  <td>
                    {/* Имя бренда стоит в обычной ячейке, а не в заголовке
                        строки, поэтому со связкой «эта кнопка про этот бренд»
                        разметке помочь нечем: подряд идущие «Удалить» звучат
                        одинаково, и человек, идущий по кнопкам, не знает,
                        какой из брендов сейчас уедет. */}
                    <button
                      type="button"
                      className="linkish"
                      aria-label={`Удалить бренд «${brand.name}»`}
                      disabled={busy.has(brand.id)}
                      onClick={() => setRemoving(brand)}
                    >
                      Удалить
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Confirm
        open={removing !== null}
        title="Удалить бренд"
        action="Удалить бренд"
        danger
        onCancel={() => setRemoving(null)}
        onConfirm={confirmRemove}
      >
        {removing ? (
          <RemoveConfirmBody
            name={removing.name}
            status={removing.status}
            questions={removing.questions_active ?? 0}
          />
        ) : null}
      </Confirm>
    </>
  );
}
